import { componentSizes } from '@jdesign-vue/constants'
import { buildProps, definePropType } from '@jdesign-vue/utils'

import type { ExtractPropTypes, VNode } from 'vue'
import type { Arrayable } from '@jdesign-vue/utils'
import type { FormItemRule } from './types'

export const formItemValidateStates = [
  '',
  'error',
  'validating',
  'success',
] as const
export type FormItemValidateState = typeof formItemValidateStates[number]

export type FormItemProp = Arrayable<string>

export const formItemProps = buildProps({
  /**
   * @description Label text.
   */
  label: String,
  /**
   * @description Width of label, e.g. `'50px'`. `'auto'` is supported.
   */
  labelWidth: {
    type: [String, Number],
    default: '',
  },
  /**
   * @description Height of label, e.g. `'50px'`. `50` is supported.
   */
  labelHeight: {
    type: [String, Number],
    default: '',
  },
  /**
   * @description  A key of `model`. It could be an array of property paths (e.g `['a', 'b', 0]`). In the use of `validate` and `resetFields` method, the attribute is required.
   */
  prop: {
    type: definePropType<FormItemProp>([String, Array]),
  },
  /**
   * @description Whether the field is required or not, will be determined by validation rules if omitted.
   */
  required: {
    type: Boolean,
    default: undefined,
  },
  /**
   * @description Validation rules of form, see the [following table](#formitemrule), more advanced usage at [async-validator](https://github.com/yiminghe/async-validator).
   */
  rules: {
    type: definePropType<Arrayable<FormItemRule>>([Object, Array]),
  },
  /**
   * @description Field error message, set its value and the field will validate error and show this message immediately.
   */
  error: String,
  /**
   * @description Validation state of formItem.
   */
  validateStatus: {
    type: String,
    values: formItemValidateStates,
  },
  /**
   * @description Same as for in native label.
   */
  for: String,
  /**
   * @description Inline style validate message.
   */
  inlineMessage: {
    type: [String, Boolean],
    default: '',
  },
  /**
   * @description Whether to show the error message.
   */
  showMessage: {
    type: Boolean,
    default: true,
  },
  /**
   * @description Control the size of components in this form-item.
   */
  size: {
    type: String,
    values: componentSizes,
  },
  staticMessage: {
    type: [String, Boolean],
    default: '',
  },
  // label隐藏是展示tooltips
  showLabelTooltips: {
    type: Boolean,
    default: true,
  },
  help: {
    type: String,
  },
  /**
   * @description 表单组件类型，用于判断是否需要设置默认样式
   */
  contentType: {
    type: definePropType<'checkbox' | 'radio' | 'switch'>(String),
    validator: (val: string) => {
      return ['checkbox', 'radio', 'switch'].includes(val)
    },
  },
  /**
   * @description Whether to enable label association with form controls. When true, label clicks will trigger associated controls.
   */
  labelAssociate: {
    type: Boolean,
    default: true,
  },
  /**
   * @description Tooltip configuration for the label. When provided, the label will be wrapped with jd-tooltip internally.
   * Accepts all jd-tooltip props (e.g. content, placement, effect, etc.)
   */
  tooltipProps: {
    type: definePropType<Record<string, any>>(Object),
  },
  /**
   * @description Tooltip content for the label. Supports string, VNode or function returning VNode.
   * When provided as VNode/function, it will be rendered in the tooltip #content slot.
   * When provided as string, it overrides tooltipProps.content.
   */
  tooltipContent: {
    type: definePropType<string | VNode | (() => VNode)>([
      String,
      Object,
      Function,
    ]),
  },
  /**
   * @description Alias for tooltipContent, kept for backward compatibility.
   * If both tooltips and tooltipContent are provided, tooltipContent takes priority.
   */
  tooltips: {
    type: definePropType<string | VNode | (() => VNode)>([
      String,
      Object,
      Function,
    ]),
  },
} as const)
export type FormItemProps = ExtractPropTypes<typeof formItemProps>
